import CloseOutlined from '@icons/CloseOutlined';
import { Autocomplete, Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { useGetUserSuggestionsQuery, useShareViewMutation } from '@service';
import { RootState } from '@store';
import { SavedView } from '@types';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

export interface ShareButtonShareModalContentsProps {
  onClose: () => void;
  view: SavedView;
}
const ShareButtonShareModalContents = ({ onClose, view }: ShareButtonShareModalContentsProps) => {
  const campaign = useSelector((state: RootState) => state.slice.campaign);
  const [acUser, setAcUser] = useState<string | null>(null);
  const [txtFieldUser, setTxtFieldUser] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const onMessageChanged = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e?.target.value;
    setMessage(value || '');
  }, []);

  const onUserSelected = useCallback((_: React.SyntheticEvent, value: string | null) => {
    setAcUser(value || null);
    setTxtFieldUser(value || '');
  }, []);

  const { currentData: userSuggestions, isFetching } = useGetUserSuggestionsQuery(
    { term: txtFieldUser, campaign_id: campaign?.id },
    {
      skip: txtFieldUser.length < 3
    }
  );
  const onUserChanged = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e?.target.value;
    setTxtFieldUser(value || '');
  }, []);
  const [shareView] = useShareViewMutation();
  const onSend = useCallback(() => {
    if (!acUser) return;
    shareView({
      user: acUser,
      view_id: view.id,
      message: ''
    })
      .unwrap()
      .then(() => {
        toast.success('View shared successfully');
        setAcUser(null);
        setTxtFieldUser('');
        setMessage('');
        onClose();
      })
      .catch(() => {
        toast.error('Error sharing view; please try again later.');
      });
  }, [acUser, onClose, shareView, view.id]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 2,
          minHeight: '56px' // pair w/ ShareButtonViewsModalContents
        }}
      >
        <Typography variant="h6" sx={{ flex: 1 }}>
          Share view
        </Typography>

        <IconButton onClick={onClose}>
          <CloseOutlined fontSize="large" />
        </IconButton>
      </Box>
      <Autocomplete
        loading={isFetching}
        noOptionsText={isFetching ? 'Loading...' : 'Enter at least 3 characters to search...'}
        blurOnSelect={false}
        renderInput={(params) => <TextField {...params} placeholder="John Doe" label="Name" onChange={onUserChanged} />}
        options={userSuggestions || []}
        value={acUser}
        onChange={onUserSelected}
      />
      <TextField multiline={true} minRows={6} label="Message (optional)" onChange={onMessageChanged} value={message} />
      <Box sx={{ flex: 1 }} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          gap: 2
        }}
      >
        <Button type="button" variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={!acUser} onClick={onSend}>
          Send
        </Button>
      </Box>
    </>
  );
};
export default ShareButtonShareModalContents;
